import React, { useEffect, useRef, useState } from 'react';
import { Trophy, Zap } from 'lucide-react';

/**
 * APEX v7.5.1 — Level Up Celebration
 *
 * Modal de célébration affichée quand l'user :
 *   - Change de tier (Diamond → Master, Master → Grandmaster, etc.)
 *   - Atteint 100 LP dans son rank actuel (= série de promotion possible)
 *
 * Auto-dismiss après 6 secondes (ou clic).
 *
 * Confetti : particules colorées qui tombent avec rotation aléatoire.
 *
 * Props :
 *   - event       : { type: 'tier_up' | 'promo_ready', from: '...', to: '...' }
 *   - onDismiss   : callback
 */

const TIER_COLORS_CONFETTI = {
  IRON: '#5d5a56',
  BRONZE: '#945a1d',
  SILVER: '#a4afb5',
  GOLD: '#cd9b30',
  PLATINUM: '#56a89d',
  EMERALD: '#1ca271',
  DIAMOND: '#3a8ed2',
  MASTER: '#9c5dd8',
  GRANDMASTER: '#cd2630',
  CHALLENGER: '#f3d077'
};

// Couleurs APEX pour fallback
const APEX_COLORS = ['#22d3ee', '#e879f9', '#a78bfa', '#fbbf24'];

function Confetti({ color }) {
  // 80 particules avec position et timings aléatoires
  const particles = Array.from({ length: 80 }).map((_, i) => ({
    id: i,
    left: Math.random() * 100, // %
    delay: Math.random() * 1.5, // s
    duration: 2.5 + Math.random() * 2, // s
    rotation: Math.random() * 720 - 360,
    size: 6 + Math.random() * 8, // px
    color: [color, ...APEX_COLORS][Math.floor(Math.random() * 5)]
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map(p => (
        <div
          key={p.id}
          className="absolute top-0"
          style={{
            left: `${p.left}%`,
            width: p.size,
            height: p.size * 0.4,
            backgroundColor: p.color,
            transform: `rotate(${p.rotation}deg)`,
            animation: `apexConfettiFall ${p.duration}s linear ${p.delay}s forwards`,
            opacity: 0
          }}
        />
      ))}
      <style>{`
        @keyframes apexConfettiFall {
          0% { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0.6; }
        }
      `}</style>
    </div>
  );
}

export default function LevelUpCelebration({ event, onDismiss }) {
  const [visible, setVisible] = useState(true);
  const dismissedRef = useRef(false);

  useEffect(() => {
    if (!event) return;
    const t = setTimeout(() => {
      if (!dismissedRef.current) {
        setVisible(false);
        setTimeout(() => onDismiss?.(), 400); // attend la fadeOut
      }
    }, 6000);
    return () => clearTimeout(t);
  }, [event, onDismiss]);

  if (!event || !visible) return null;

  const handleClick = () => {
    dismissedRef.current = true;
    setVisible(false);
    setTimeout(() => onDismiss?.(), 400);
  };

  const isTierUp = event.type === 'tier_up';
  const isPromoReady = event.type === 'promo_ready';

  // Couleur thème
  const themeTier = isTierUp ? event.to?.toUpperCase() : event.tier?.toUpperCase();
  const themeColor = TIER_COLORS_CONFETTI[themeTier] || '#22d3ee';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={handleClick}
      style={{ animation: visible ? 'apexCelebFadeIn 0.3s ease-out' : 'apexCelebFadeOut 0.4s ease-out' }}
    >
      {/* Confetti */}
      <Confetti color={themeColor} />

      {/* Carte centrale */}
      <div
        className="relative bg-zinc-950 border-2 px-12 py-10 max-w-md text-center"
        style={{
          borderColor: themeColor,
          boxShadow: `0 0 60px -10px ${themeColor}99, inset 0 0 60px -20px ${themeColor}40`,
          animation: 'apexCelebPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)'
        }}
      >
        {/* Halo radial */}
        <div
          className="absolute inset-0 pointer-events-none opacity-20"
          style={{
            background: `radial-gradient(circle, ${themeColor} 0%, transparent 70%)`
          }}
        />

        <div className="relative">
          {/* Icon */}
          <div className="mb-4 flex justify-center">
            {isTierUp ? (
              <Trophy size={48} style={{ color: themeColor, filter: `drop-shadow(0 0 12px ${themeColor})` }} />
            ) : (
              <Zap size={48} style={{ color: themeColor, filter: `drop-shadow(0 0 12px ${themeColor})` }} />
            )}
          </div>

          {/* Title */}
          {isTierUp && (
            <>
              <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 font-bold mb-2">
                🚀 Nouveau Tier
              </div>
              <div className="text-3xl font-black mb-1" style={{ color: themeColor, textShadow: `0 0 20px ${themeColor}` }}>
                {event.to}
              </div>
              <div className="text-sm text-zinc-400 font-mono">
                {event.from} → <span style={{ color: themeColor }}>{event.to}</span>
              </div>
            </>
          )}
          {isPromoReady && (
            <>
              <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 font-bold mb-2">
                ⚡ Série de promotion
              </div>
              <div className="text-3xl font-black mb-1" style={{ color: themeColor, textShadow: `0 0 20px ${themeColor}` }}>
                100 LP
              </div>
              <div className="text-sm text-zinc-400 font-mono">
                Prochaine game → série pour <span style={{ color: themeColor }}>{event.nextTier}</span>
              </div>
            </>
          )}

          <div className="text-[10px] text-zinc-600 font-mono uppercase tracking-wider mt-6">
            Clic pour fermer
          </div>
        </div>
      </div>

      <style>{`
        @keyframes apexCelebPop {
          0% { transform: scale(0.6); opacity: 0; }
          70% { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes apexCelebFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes apexCelebFadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
      `}</style>
    </div>
  );
}
